/**
 * The pilot hazard pack's landing page (`/hazard-pack/<token>`; Diurnal parity 04-056, the tool's feed landing page,
 * hazard_feed.js `generateLandingPage`; decisions 88, 625, 808; docs/specs/cloud/hazard-packs.md). Public, no sign-in
 * and no tenant: the link is the key. It says whose pack it is, when it was published and its version (so a pilot can
 * tell whether their copy is current), what it holds and how long the link works, with one button, "Download for Oz
 * Runways", and the tool's steps to load it under KML Overlays. Oz Runways does not update the pack by itself
 * (decision 808): the pilot downloads it again after each new publish. A withdrawn or expired link says so, and
 * nothing of the pack is shown. Supabase serves no HTML from its own address, so the page is Teslyn Cloud's and reads the details from the
 * `hazard-pack` function.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  hazardPackContents,
  hazardPackExpiryWords,
  hazardPackSteps,
  hazardPackUpdateWords,
  hazardPackVersionWords,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PoweredBy } from '../components/States.tsx';
import { LegalLinks } from '../legal/LegalPage.tsx';
import { loadLanding, type LandingAnswer } from './api.ts';

export interface HazardPackPageProps {
  readonly token: string;
  readonly supabaseUrl: string;
  readonly fetchImpl?: (url: string, init?: RequestInit) => Promise<Response>;
  readonly now?: () => number;
  /** This page's address, for "Copy link". */
  readonly href?: string;
}

export function HazardPackPage({
  token,
  supabaseUrl,
  fetchImpl,
  now = Date.now,
  href = window.location.href,
}: HazardPackPageProps): ReactElement {
  const [answer, setAnswer] = useState<LandingAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setError(null);
    loadLanding(supabaseUrl, token, fetchImpl)
      .then(setAnswer)
      .catch((e: unknown) =>
        setError(e instanceof Error && e.message ? e.message : 'Teslyn Cloud could not be reached.'),
      );
  }, [supabaseUrl, token, fetchImpl]);
  useEffect(load, [load]);

  useEffect(() => {
    document.title =
      answer?.kind === 'pack'
        ? `${answer.meta.project_number} hazard pack for Oz Runways`
        : 'Hazard pack for Oz Runways';
  }, [answer]);

  function body(): ReactElement {
    if (error) return <ErrorState message={error} onRetry={load} />;
    if (!answer) return <Loading what="the hazard pack" />;
    if (answer.kind === 'unknown')
      return (
        <>
          <h1 className="title">Hazard pack not found</h1>
          <p className="body" role="alert">
            {answer.message}
          </p>
        </>
      );
    const meta = answer.meta;
    const expiry = hazardPackExpiryWords(meta, now());
    if (meta.state !== 'live')
      return (
        <>
          <p className="label">{meta.organisation}</p>
          <h1 className="title">{meta.project_number} hazard pack: the link no longer works</h1>
          <p className="body" role="alert">
            {expiry} Ask the office for the current link. If you added this pack to Oz Runways, remove it from
            KML Overlays: it may be out of date.
          </p>
        </>
      );
    const contents = meta.stats ? hazardPackContents(meta.stats) : [];
    const steps = hazardPackSteps(meta.files);
    return (
      <>
        <p className="label">{meta.organisation}</p>
        <h1 className="title">Hazard pack for Oz Runways</h1>
        <p className="body">
          Project <span className="data">{meta.project_number}</span>: {meta.name}
        </p>
        <p className="body" role="status">
          {hazardPackVersionWords(meta.published_at, meta.version)}. If the copy in your Oz Runways is older,
          download it again.
        </p>
        <dl className="pack-landing__facts caption">
          <dt>Link</dt>
          <dd>{expiry}</dd>
        </dl>
        {contents.length > 0 && (
          <>
            <h2 className="heading">What it holds</h2>
            <ul className="body" aria-label="What it holds">
              {contents.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </>
        )}
        <div className="pack-landing__actions">
          <a className="btn btn--primary" href={meta.urls.kml} download={meta.files.kml}>
            Download for Oz Runways
          </a>
          <Button
            variant="quiet"
            onClick={() =>
              void navigator.clipboard
                .writeText(href)
                .then(() => setCopied(true))
                .catch(() => setCopied(false))
            }
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>
        <h2 className="heading">How to load it in Oz Runways</h2>
        <ol className="body" aria-label="How to load it">
          {steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <p className="notice notice--warn caption">{hazardPackUpdateWords(meta.files)}</p>
        <p className="caption muted">
          Hazards are advisory: powerlines, towers and obstacles may be missing or out of date. Never rely on
          this pack as the only source for flight safety.
        </p>
      </>
    );
  }

  return (
    <div className="signin">
      <main className="signin__card pack-landing" aria-label="Hazard pack">
        {body()}
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks />
      </footer>
    </div>
  );
}
